import React, { useState } from 'react';
import { XMarkIcon, TrashIcon, CodeBracketIcon, EyeIcon } from '@heroicons/react/24/outline';
import { getNodeTypeInfo } from './editorUtils';
import { useNodeTypeRegistry } from './NodeTypeContext';

import GenericConfig from './configs/GenericConfig';
import VisualParamsEditor from './configs/VisualParamsEditor';

const NodeConfigPanel = ({ node, onUpdateParameters, onUpdateName, onDelete, onClose }) => {
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(node.name);
  const [editMode, setEditMode] = useState('visual'); // 'visual' or 'json'
  const registry = useNodeTypeRegistry();

  const typeInfo = getNodeTypeInfo(node.type, registry);

  const handleNameSubmit = () => {
    if (nameValue.trim() && nameValue !== node.name) {
      onUpdateName(node.id, nameValue.trim());
    }
    setEditingName(false);
  };

  React.useEffect(() => {
    setNameValue(node.name);
    setEditingName(false);
  }, [node.id, node.name]);

  return (
    <div className="w-[420px] flex-shrink-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg">{typeInfo?.icon || '⚙️'}</span>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
            {typeInfo?.label || node.type?.split('.').pop()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onDelete}
            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md text-gray-400 hover:text-red-500 transition-colors"
            title="Delete node"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Node Name */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
            Name
          </label>
          {editingName ? (
            <input
              type="text"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={(e) => { if (e.key === 'Enter') handleNameSubmit(); if (e.key === 'Escape') { setNameValue(node.name); setEditingName(false); } }}
              autoFocus
              className="w-full px-3 py-2 border border-violet-400 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          ) : (
            <button
              onClick={() => setEditingName(true)}
              className="w-full text-left px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white hover:border-violet-400 transition-colors"
            >
              {node.name}
            </button>
          )}
        </div>

        {/* Node Notes */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
            Notes
          </label>
          <textarea
            value={node.notes || ''}
            onChange={(e) => {
              onUpdateParameters(node.id, { ...node.parameters, _notes: e.target.value });
            }}
            placeholder="Add notes about this step..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
        </div>

        {/* Divider + mode toggle */}
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Parameters
          </label>
          <div className="inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded-md p-0.5">
            <button
              onClick={() => setEditMode('visual')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded transition-colors ${
                editMode === 'visual'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
              }`}
            >
              <EyeIcon className="w-3 h-3" />
              Visual
            </button>
            <button
              onClick={() => setEditMode('json')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded transition-colors ${
                editMode === 'json'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
              }`}
            >
              <CodeBracketIcon className="w-3 h-3" />
              JSON
            </button>
          </div>
        </div>

        {/* Parameters editor */}
        {editMode === 'visual' ? (
          <VisualParamsEditor
            parameters={node.parameters || {}}
            onChange={(newParams) => onUpdateParameters(node.id, newParams)}
            nodeTypeKey={typeInfo?.key || ''}
          />
        ) : (
          <GenericConfig
            parameters={node.parameters || {}}
            onChange={(newParams) => onUpdateParameters(node.id, newParams)}
          />
        )}
      </div>
    </div>
  );
};

export default NodeConfigPanel;
